import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ConfirmModal, StateView, type MenuAction } from '@/card/record'
import { RecordPicker } from '@/card/RecordPicker'
import { Pager, useClientPages } from '@/lists/Pager'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  PrimaryButton,
  RowMenu,
  SearchBox,
  SkeletonTable,
  initials,
  isForbidden,
  useAdminEditing,
} from './AdminShell'
import {
  assignAccessRole,
  createAccessUser,
  fetchAccessRoles,
  fetchAccessUsers,
  revokeAccessRole,
  updateAccessUser,
  type AccessRole,
  type AccessUser,
} from './api'

/**
 * Учётные записи (тикет #102, макет AdmUsers.dc.html): привязка к сотруднику, блокировка,
 * выдача ролей — в том числе временная.
 *
 * Сотрудник выбирается поиском, а не идентификатором: от привязки зависит область `own`, и
 * ошибка в ней тихая — пользователь видит чужие записи или не видит своих. Пароля здесь нет:
 * вход через Keycloak, первый вход свяжет учётку по логину.
 *
 * «Последний вход» — последний запрос с действующим токеном (точность — интервал отметки,
 * пять минут), а не момент ввода пароля: вход видит Keycloak. Пусто — не приходили ни разу
 * с тех пор, как отметка ведётся, и это сказано словами.
 *
 * Последнего действующего суперадмина сервер не даст заблокировать и лишить роли. Экран
 * показывает это заранее — пункт меню виден, но недоступен, с причиной словами: спрятанный
 * пункт выглядел бы как нехватка прав у того, кто смотрит.
 */

const SUPERADMIN = 'SUPERADMIN'
type Status = 'all' | 'active' | 'blocked'

/** Бессрочная роль «Суперадмин» у действующей учётки — то, что сервер считает «действующим». */
const holdsSuperadmin = (user: AccessUser) =>
  user.active && user.roles.some((role) => role.roleCode === SUPERADMIN && role.expiresAt == null)

export function UsersPage() {
  const { t, locale, user: me } = useApp()
  const editing = useAdminEditing('system.user')
  const queryClient = useQueryClient()
  // The whole list, filtered here: "is this the last superadmin" is a question about all
  // accounts, and a server-side search would answer it about the matching ones only.
  const users = useQuery({ queryKey: ['admin-users'], queryFn: ({ signal }) => fetchAccessUsers('', signal) })
  const roles = useQuery({ queryKey: ['admin-roles'], queryFn: ({ signal }) => fetchAccessRoles(signal) })
  const [query, setQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [status, setStatus] = useState<Status>('all')
  const [temporaryOnly, setTemporaryOnly] = useState(false)
  const [creating, setCreating] = useState(false)
  const [editingUser, setEditingUser] = useState<AccessUser | null>(null)
  const [granting, setGranting] = useState<AccessUser | null>(null)
  const [blocking, setBlocking] = useState<AccessUser | null>(null)

  const refresh = () => {
    editing.clear()
    void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    void queryClient.invalidateQueries({ queryKey: ['admin-roles'] })
  }
  const revoke = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) => revokeAccessRole(userId, roleId),
    onSuccess: refresh,
    onError: editing.fail,
  })
  const setActive = useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) => updateAccessUser(userId, { active }),
    onSuccess: () => {
      setBlocking(null)
      refresh()
    },
    onError: (error) => {
      setBlocking(null)
      editing.fail(error)
    },
  })
  const unlinkSso = useMutation({
    mutationFn: (userId: string) => updateAccessUser(userId, { externalSubject: '' }),
    onSuccess: refresh,
    onError: editing.fail,
  })

  const all = users.data ?? []
  const superadmins = all.filter(holdsSuperadmin).length
  const lastSuperadmin = (user: AccessUser) => holdsSuperadmin(user) && superadmins <= 1
  const temporary = (user: AccessUser) => user.roles.some((role) => role.expiresAt != null && !role.expired)

  const needle = query.trim().toLowerCase()
  const bySearch = all.filter((user) =>
    !needle || [user.fullName, user.username, user.email ?? ''].some((value) => value.toLowerCase().includes(needle)),
  )
  const byRole = bySearch.filter((user) => !roleFilter || user.roles.some((role) => role.roleId === roleFilter))
  const counts = {
    all: byRole.length,
    active: byRole.filter((user) => user.active).length,
    blocked: byRole.filter((user) => !user.active).length,
  }
  const shown = byRole
    .filter((user) => status === 'all' || (status === 'active' ? user.active : !user.active))
    .filter((user) => !temporaryOnly || temporary(user))
  const { visible, pager } = useClientPages(shown, 25)
  const filtered = Boolean(needle || roleFilter || status !== 'all' || temporaryOnly)
  const forbidden = isForbidden(users.error)
  const mayReadEmployees = can(me, 'hr.employee', 'read')

  const menuFor = (user: AccessUser): MenuAction[] => {
    const items: MenuAction[] = [
      { id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setEditingUser(user) },
      { id: 'grant', label: t('adm.users.grant'), icon: 'plus', onSelect: () => setGranting(user) },
      ...user.roles.map((role): MenuAction => ({
        id: `revoke-${role.roleId}`,
        label: t('adm.users.revokeRole', { role: role.roleName }),
        icon: 'adm-minus',
        disabledReason: role.roleCode === SUPERADMIN && lastSuperadmin(user) ? t('adm.users.lastSuperadmin.revoke') : undefined,
        onSelect: () => revoke.mutate({ userId: user.id, roleId: role.roleId }),
      })),
    ]
    if (user.keycloakLinked && !user.external) {
      items.push({ id: 'sso', label: t('adm.users.unlinkSso'), icon: 'adm-key', onSelect: () => unlinkSso.mutate(user.id) })
    }
    items.push(
      user.active
        ? {
            id: 'block',
            label: t('admin.users.block'),
            icon: 'lock',
            danger: true,
            disabledReason: lastSuperadmin(user) ? t('adm.users.lastSuperadmin.block') : undefined,
            onSelect: () => setBlocking(user),
          }
        : {
            id: 'unblock',
            label: t('admin.users.unblock'),
            icon: 'check',
            onSelect: () => setActive.mutate({ userId: user.id, active: true }),
          },
    )
    return items
  }

  return (
    <AdminPage
      titleKey="nav.adminUsers"
      count={users.data ? all.length.toLocaleString(locale) : undefined}
      leadKey="adm.users.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.users.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && <PrimaryButton label={t('admin.users.add')} onClick={() => setCreating(true)} />}
    >
      <Notice text={editing.notice} />
      {!forbidden && (
        <div className="toolbar">
          <SearchBox value={query} onChange={setQuery} label={t('admin.users.search')} width={280} />
          <select className="input input-sm" style={{ width: 210 }} aria-label={t('adm.users.roleFilter')}
                  value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
            <option value="">{t('adm.users.roleAll')}</option>
            {(roles.data ?? []).map((role) => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>
          <div className="seg" role="group" aria-label={t('adm.users.status')}>
            {(['all', 'active', 'blocked'] as const).map((key) => (
              <button key={key} type="button" aria-pressed={status === key} onClick={() => setStatus(key)}>
                {t(`adm.users.status.${key}`)} <span className="muted">{counts[key]}</span>
              </button>
            ))}
          </div>
          <button type="button" className="chip" aria-pressed={temporaryOnly} onClick={() => setTemporaryOnly(!temporaryOnly)}>
            <Icon name="clock" />
            {t('adm.users.temporary')} <span className="count">{byRole.filter(temporary).length}</span>
          </button>
        </div>
      )}
      <AdminState
        isLoading={users.isLoading}
        error={users.error}
        onRetry={() => void users.refetch()}
        forbiddenTitleKey="adm.users.forbidden"
        errorTitleKey="adm.users.error"
        skeleton={<SkeletonTable columns={6} rows={12} />}
      >
        {shown.length === 0 ? (
          <StateView
            boxed
            kind="empty"
            title={filtered ? t('adm.users.emptyFiltered') : t('adm.users.empty')}
            description={filtered ? t('adm.users.emptyFiltered.text') : undefined}
            actions={
              filtered && (
                <>
                  {status !== 'blocked' && counts.blocked > 0 && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStatus('blocked')}>
                      {t('adm.users.showBlocked')}
                    </button>
                  )}
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => {
                    setQuery('')
                    setRoleFilter('')
                    setStatus('all')
                    setTemporaryOnly(false)
                  }}>
                    {t('adm.resetFilters')}
                  </button>
                </>
              )
            }
          />
        ) : (
          <>
            <div className="tbl-wrap">
              <table className="tbl" aria-label={t('nav.adminUsers')}>
                <thead>
                  <tr>
                    <th>{t('admin.users.fullName')}</th>
                    <th>{t('admin.users.username')}</th>
                    <th>{t('adm.users.contact')}</th>
                    <th>{t('admin.users.roles')}</th>
                    <th>{t('adm.users.card')}</th>
                    <th>{t('adm.users.lastLogin')}</th>
                    <th>{t('adm.status')}</th>
                    <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((user) => (
                    <tr key={user.id} className={user.active ? undefined : 'is-muted'}>
                      <td>
                        <span className="row" style={{ gap: 8 }}>
                          <span className={user.id === me?.userId ? 'ava ava-me' : 'ava'}>{initials(user.fullName)}</span>
                          <span className="stack" style={{ gap: 0 }}>
                            <span>{user.fullName}</span>
                            {user.keycloakLinked ? (
                              <span className="t-xs muted row" style={{ gap: 4 }}>
                                <Icon name="link" size="xs" />
                                {t('adm.users.sso')}
                              </span>
                            ) : (
                              <span className="t-xs muted">{t('admin.users.notLinked')}</span>
                            )}
                            {user.external && <span className="t-xs muted">{t('adm.users.external')}</span>}
                          </span>
                        </span>
                      </td>
                      <td><span className="mono">{user.username}</span></td>
                      <td>
                        {user.email || user.phone ? (
                          <span className="stack" style={{ gap: 2 }}>
                            {user.email && <span className="row" style={{ gap: 6 }}><Icon name="mail" size="sm" className="muted" />{user.email}</span>}
                            {user.phone && <span className="row t-sm" style={{ gap: 6 }}><Icon name="phone" size="sm" className="muted" />{user.phone}</span>}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="wrap">
                        <span className="row-wrap" style={{ gap: 4 }}>
                          {user.roles.length === 0 && <span className="muted">{t('adm.users.noRoles')}</span>}
                          {user.roles.map((role) => (
                            <span key={role.roleId}
                                  className={role.expired ? 'badge' : role.expiresAt ? 'badge b-warning' : role.roleCode === SUPERADMIN ? 'badge b-accent' : 'badge'}>
                              {role.expiresAt && <Icon name="clock" />}
                              {role.roleName}
                              {role.expiresAt && (role.expired
                                ? ` · ${t('admin.users.expired')}`
                                : ` · ${t('admin.users.until', { date: formatDate(role.expiresAt, locale) })}`)}
                            </span>
                          ))}
                        </span>
                      </td>
                      <td>
                        {user.employeeId ? (
                          mayReadEmployees ? (
                            <Link to={`/employees/${user.employeeId}`} className="row" style={{ gap: 4 }}>
                              <Icon name="user" size="sm" />
                              {user.employeeName ?? user.employeeId}
                            </Link>
                          ) : (
                            <span className="row" style={{ gap: 4 }}><Icon name="user" size="sm" />{user.employeeName}</span>
                          )
                        ) : (
                          <span className="muted">{t('admin.users.noEmployee')}</span>
                        )}
                      </td>
                      <td className="nowrap">
                        {user.lastLoginAt ? (
                          <span title={new Date(user.lastLoginAt).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')}>
                            {formatDate(user.lastLoginAt, locale)}
                          </span>
                        ) : (
                          <span className="muted">{t('adm.users.neverSeen')}</span>
                        )}
                      </td>
                      <td>
                        {user.active ? (
                          <span className="badge b-success"><Icon name="check" />{t('admin.users.active')}</span>
                        ) : (
                          <span className="badge"><Icon name="lock" />{t('admin.users.blocked')}</span>
                        )}
                      </td>
                      <td className="adm-act">
                        {editing.mayEdit && (
                          <RowMenu label={t('adm.users.actions', { name: user.username })} items={menuFor(user)} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager {...pager} />
          </>
        )}
      </AdminState>

      {creating && (
        <NewUserModal roles={roles.data ?? []} onClose={() => setCreating(false)} onSaved={() => {
          setCreating(false)
          refresh()
        }} />
      )}
      {editingUser && (
        <EditUserSheet user={editingUser} onClose={() => setEditingUser(null)} onSaved={() => {
          setEditingUser(null)
          refresh()
        }} />
      )}
      {granting && (
        <GrantRoleSheet user={granting} roles={roles.data ?? []} onClose={() => setGranting(null)} onSaved={() => {
          setGranting(null)
          refresh()
        }} />
      )}
      <ConfirmModal
        open={blocking != null}
        icon="lock"
        title={t('adm.users.blockConfirm', { name: blocking?.fullName ?? '' })}
        confirmLabel={t('admin.users.block')}
        pending={setActive.isPending}
        onCancel={() => setBlocking(null)}
        onConfirm={() => blocking && setActive.mutate({ userId: blocking.id, active: false })}
      >
        <p>{t('adm.users.blockConfirm.text')}</p>
      </ConfirmModal>
    </AdminPage>
  )
}

function formatDate(iso: string, locale: string) {
  return new Date(iso).toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU')
}

/** Дата без времени — «до конца этого дня» в поясе браузера. */
const endOfDay = (date: string) => new Date(`${date}T23:59:59`).toISOString()

function NewUserModal({ roles, onClose, onSaved }: { roles: AccessRole[]; onClose: () => void; onSaved: () => void }) {
  const { t } = useApp()
  const formId = useId()
  const [draft, setDraft] = useState({ username: '', fullName: '', email: '', phone: '' })
  const [employee, setEmployee] = useState<{ id: string; label: string } | null>(null)
  const [picked, setPicked] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [contactMissing, setContactMissing] = useState(false)
  // Superadmin is granted by its own action on an existing account, never in passing.
  const offered = roles.filter((role) => role.code !== SUPERADMIN && !picked.includes(role.id))

  const create = useMutation({
    mutationFn: async () => {
      const user = await createAccessUser({
        username: draft.username,
        fullName: draft.fullName,
        email: draft.email || null,
        phone: draft.phone || null,
        employeeId: employee?.id ?? null,
      })
      for (const roleId of picked) await assignAccessRole(user.id, roleId, null)
      return user
    },
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="modal"
      overline={t('adm.users.overline')}
      title={t('admin.users.form')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('adm.users.create')} pending={create.isPending} form={formId} />}
    >
      <form id={formId} className="form-grid" aria-label={t('admin.users.form')} onSubmit={(event) => {
        event.preventDefault()
        // The server refuses an account without a contact as well; said here, under the
        // fields, instead of after a round trip.
        if (!draft.email.trim() && !draft.phone.trim()) {
          setContactMissing(true)
          return
        }
        setError(null)
        create.mutate()
      }}>
        <Field label={t('admin.users.fullName')} htmlFor="nu-name" required span2>
          <input id="nu-name" className="input" value={draft.fullName} onChange={(e) => setDraft({ ...draft, fullName: e.target.value })} />
        </Field>
        <Field label={t('admin.users.username')} htmlFor="nu-login" required help={t('adm.users.loginHelp')} span2>
          <input id="nu-login" className="input mono" value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })} />
        </Field>
        <Field label={t('admin.users.email')} htmlFor="nu-mail">
          <input id="nu-mail" type="email" className={contactMissing ? 'input is-invalid' : 'input'} aria-invalid={contactMissing}
                 value={draft.email} onChange={(e) => {
                   setContactMissing(false)
                   setDraft({ ...draft, email: e.target.value })
                 }} />
        </Field>
        <Field label={t('adm.users.phone')} htmlFor="nu-phone">
          <input id="nu-phone" type="tel" className={contactMissing ? 'input is-invalid' : 'input'} aria-invalid={contactMissing}
                 placeholder="+7 ___ ___-__-__" value={draft.phone} onChange={(e) => {
                   setContactMissing(false)
                   setDraft({ ...draft, phone: e.target.value })
                 }} />
        </Field>
        {contactMissing && (
          <div className="span-2">
            <span className="err-text" role="alert"><Icon name="adm-error" size="sm" />{t('adm.users.contactRequired')}</span>
          </div>
        )}
        <Field label={t('admin.users.roles')} span2 help={t('adm.users.rolesHelp')}>
          <div className="input" style={{ height: 'auto', minHeight: 'var(--control-h)', flexWrap: 'wrap', gap: 4, padding: '3px 4px 3px 6px' }}>
            {picked.map((roleId) => {
              const role = roles.find((candidate) => candidate.id === roleId)
              return (
                <span key={roleId} className="badge">
                  {role?.name ?? roleId}
                  <button type="button" className="btn btn-ghost btn-sm btn-icon" style={{ width: 16, height: 16, marginRight: -4 }}
                          aria-label={t('adm.users.removeRole', { role: role?.name ?? '' })}
                          onClick={() => setPicked(picked.filter((id) => id !== roleId))}>
                    <Icon name="close" size="xs" />
                  </button>
                </span>
              )
            })}
            <select className="adm-bare" aria-label={t('adm.users.addRole')} value=""
                    onChange={(e) => e.target.value && setPicked([...picked, e.target.value])}>
              <option value="">{t('adm.users.addRole')}</option>
              {offered.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={t('adm.users.linkEmployee')} htmlFor="user-employee" span2 help={t('adm.users.linkEmployeeHelp')}>
          <RecordPicker id="user-employee" entity="hr.employee" searchColumn="last_name" extraColumn="first_name"
                        value={employee} onChange={setEmployee} />
        </Field>
        {error && <div className="span-2"><Notice text={error} /></div>}
      </form>
    </EditLayer>
  )
}

function EditUserSheet({ user, onClose, onSaved }: { user: AccessUser; onClose: () => void; onSaved: () => void }) {
  const { t } = useApp()
  const formId = useId()
  const [fullName, setFullName] = useState(user.fullName)
  const [email, setEmail] = useState(user.email ?? '')
  const [phone, setPhone] = useState(user.phone ?? '')
  const [employee, setEmployee] = useState<{ id: string; label: string } | null>(
    user.employeeId ? { id: user.employeeId, label: user.employeeName ?? user.employeeId } : null,
  )
  const [error, setError] = useState<string | null>(null)

  // Only what changed goes to the server: a PUT that repeats every field would overwrite a
  // colleague's edit made a minute ago with what this sheet read before it.
  const body = useMemo(() => {
    const changes: Record<string, unknown> = {}
    if (fullName.trim() !== user.fullName) changes.fullName = fullName
    if (email.trim() !== (user.email ?? '')) changes.email = email
    if (phone.trim() !== (user.phone ?? '')) changes.phone = phone
    if ((employee?.id ?? null) !== user.employeeId) {
      if (employee) changes.employeeId = employee.id
      else changes.clearEmployee = true
    }
    return changes
  }, [fullName, email, phone, employee, user])

  const save = useMutation({
    mutationFn: () => updateAccessUser(user.id, body),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="sheet"
      overline={t('adm.users.sheet')}
      title={`${t('admin.edit')} — ${user.fullName}`}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending}
                            disabled={Object.keys(body).length === 0} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('adm.users.editForm')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <Field label={t('admin.users.username')}>
          <div className="input is-readonly mono" aria-readonly="true">{user.username}</div>
        </Field>
        <Field label={t('admin.users.fullName')} htmlFor="eu-name" required>
          <input id="eu-name" className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>
        <Field label={t('admin.users.email')} htmlFor="eu-mail">
          <input id="eu-mail" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={t('adm.users.phone')} htmlFor="eu-phone">
          <input id="eu-phone" type="tel" className="input" placeholder="+7 ___ ___-__-__" value={phone}
                 onChange={(e) => setPhone(e.target.value)} />
        </Field>
        {!user.external && (
          <Field label={t('adm.users.linkEmployee')} htmlFor="eu-employee" help={t('adm.users.linkEmployeeHelp')}>
            <RecordPicker id="eu-employee" entity="hr.employee" searchColumn="last_name" extraColumn="first_name"
                          value={employee} onChange={setEmployee} />
          </Field>
        )}
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}

function GrantRoleSheet({ user, roles, onClose, onSaved }: {
  user: AccessUser
  roles: AccessRole[]
  onClose: () => void
  onSaved: () => void
}) {
  const { t, locale } = useApp()
  const formId = useId()
  const [roleId, setRoleId] = useState('')
  const [until, setUntil] = useState('')
  const [error, setError] = useState<string | null>(null)
  const held = new Set(user.roles.filter((role) => !role.expired).map((role) => role.roleId))
  const assign = useMutation({
    mutationFn: () => assignAccessRole(user.id, roleId, until ? endOfDay(until) : null),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  const current = user.roles.filter((role) => !role.expired).map((role) => role.roleName).join(', ')

  return (
    <EditLayer
      kind="sheet"
      overline={t('adm.users.sheet')}
      title={t('adm.users.grantTitle', { name: user.fullName })}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('adm.users.grantSave')} pending={assign.isPending}
                            disabled={roleId === ''} form={formId} />}
    >
      <div className="row" style={{ gap: 10 }}>
        <span className="ava ava-lg">{initials(user.fullName)}</span>
        <span className="stack" style={{ gap: 0 }}>
          <span className="strong">{user.fullName}</span>
          <span className="t-sm muted">
            <span className="mono">{user.username}</span>
            {current && ` · ${t('adm.users.now', { roles: current })}`}
          </span>
        </span>
      </div>
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('adm.users.grantForm')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        assign.mutate()
      }}>
        <Field label={t('adm.users.role')} htmlFor="gr-role" required help={current ? t('adm.users.addsTo', { roles: current }) : undefined}>
          <select id="gr-role" className="input" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">{t('admin.users.chooseRole')}</option>
            {roles.filter((role) => !held.has(role.id)).map((role) => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>
        </Field>
        <Field label={t('adm.users.until')} htmlFor="gr-until" help={t('adm.users.untilHelp')}>
          <input id="gr-until" type="date" className="input" value={until} onChange={(e) => setUntil(e.target.value)} />
        </Field>
        {until && (
          <div className="alert alert-neutral">
            <Icon name="adm-info" />
            <span>{t('adm.users.untilNote', { date: formatDate(endOfDay(until), locale) })}</span>
          </div>
        )}
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
